import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import "./NPSModal.css";
import "./EncuestaCorreo.css";
import { configShop } from "../../states/configShop";

/**
 * PLZ-234: pagina de la encuesta NPS que llega por correo (SPEC-ENCUESTA-CORREO §4 y §9).
 * - /encuesta/:enlace?s=N  → escala 0 a 10 con el puntaje del correo ya marcado. Abrir NO guarda nada:
 *   los filtros de seguridad de Gmail y Outlook abren los enlaces y no deben calificar solos.
 * - /encuesta/:enlace/baja → confirmar para darse de baja (abrir tampoco guarda).
 * - Los datos (comercio, logo, color, pedido) salen de la invitacion en api-menu-digital, no del subdominio.
 * - "prueba" es el enlace del correo de prueba del Admin: muestra la pagina sin llamar ni guardar nada.
 */

const DEMO = "prueba";
const DEFAULT_COLOR = "#2c3e50";

async function surveyRequest(path, body) {
    const res = await fetch(process.env.REACT_APP_API_URL + path, {
        method: body === undefined ? "GET" : "POST",
        headers: { "Content-Type": "application/json" },
        body: body === undefined ? undefined : JSON.stringify(body),
    });
    let json = null;
    try {
        json = await res.json();
    } catch (e) {
        // Respuesta que no es JSON (p. ej. 404 en HTML)
    }
    if (!json) throw new Error(`HTTP ${res.status}`);
    return json;
}

/** ?s= del correo: entero de 0 a 10, o nada. */
export function parseScore(value) {
    if (value === null || value === undefined || !/^\d{1,2}$/.test(String(value))) return null;
    const n = Number(value);
    return n >= 0 && n <= 10 ? n : null;
}

/** De rojo (0) a verde (10), como los botones del correo. */
function scoreColor(score) {
    return `hsl(${score * 12}, 75%, 45%)`;
}

function useDemoCompany() {
    const { dataConfig } = configShop();
    return {
        name: dataConfig?.company_name || "Tu comercio",
        logo: dataConfig?.company_logo || null,
        color: dataConfig?.color_botton || DEFAULT_COLOR,
    };
}

function Shell({ company, children, demo }) {
    return (
        <div className="encuesta-page">
            <div className="encuesta-card">
                {demo && <div className="encuesta-demo">Vista de prueba: nada de lo que hagas aquí se guarda.</div>}
                {company && (
                    <div className="encuesta-brand">
                        {company.logo && <img className="encuesta-logo" src={company.logo} alt={company.name} />}
                        <span className="encuesta-company">{company.name}</span>
                    </div>
                )}
                {children}
            </div>
            <a className="encuesta-powered" href="https://sealky.co" target="_blank" rel="noopener noreferrer">
                Powered by <strong>Sealky</strong>
            </a>
        </div>
    );
}

function Message({ emoji, title, text }) {
    return (
        <div className="nps-thanks">
            <div className="nps-thanks-emoji">{emoji}</div>
            <h2 className="nps-thanks-title">{title}</h2>
            {text && <p className="nps-thanks-sub">{text}</p>}
        </div>
    );
}

const STATE_MESSAGES = {
    invalid: { emoji: "🔗", title: "Este enlace no es válido", text: "Revisa que hayas abierto el enlace completo del correo." },
    expired: { emoji: "⏳", title: "Este enlace ya venció", text: "Las encuestas se pueden responder hasta 30 días después del pedido." },
    answered: { emoji: "🙌", title: "Ya habías calificado este pedido", text: "¡Gracias por tomarte el tiempo!" },
};

export function EncuestaCorreo() {
    const { enlace } = useParams();
    const [searchParams] = useSearchParams();
    const demo = enlace === DEMO;
    const demoCompany = useDemoCompany();

    const [data, setData] = useState(null);
    const [loadError, setLoadError] = useState(false);
    const [score, setScore] = useState(parseScore(searchParams.get("s")));
    const [comment, setComment] = useState("");
    const [sending, setSending] = useState(false);
    const [sendError, setSendError] = useState("");
    const [done, setDone] = useState(null); // {emoji,title,text}

    const load = async () => {
        setLoadError(false);
        if (demo) {
            setData({ state: "pending", company: demoCompany, order_number: 12345, branch_name: "Sucursal principal" });
            return;
        }
        try {
            const res = await surveyRequest(`/survey/email/${encodeURIComponent(enlace)}`);
            setData(res?.data?.state ? res.data : { state: "invalid" });
        } catch (e) {
            setLoadError(true);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enlace]);

    const company = data?.company || (demo ? demoCompany : null);
    const brand = company?.color || DEFAULT_COLOR;

    const handleSend = async () => {
        if (score === null || sending) return;
        setSendError("");
        const thanks = score >= 9
            ? { emoji: "🎉", title: "¡Nos alegra mucho!", text: "Tu calificación nos motiva a seguir dando lo mejor." }
            : { emoji: "🙏", title: "¡Gracias por tu honestidad!", text: "Usaremos tu opinión para mejorar tu próxima experiencia." };
        if (demo) {
            setDone(thanks);
            return;
        }
        setSending(true);
        try {
            const res = await surveyRequest(`/survey/email/${encodeURIComponent(enlace)}`, { score, comment: comment.trim() });
            const state = res?.data?.state;
            if (res?.success === "success" && state === "answered" && res?.message?.startsWith("Ya hab")) {
                setDone(STATE_MESSAGES.answered);
            } else if (res?.success === "success") {
                setDone(thanks);
            } else if (state && STATE_MESSAGES[state]) {
                setDone(STATE_MESSAGES[state]);
            } else {
                setSendError(res?.message || "No pudimos registrar tu calificación. Intenta de nuevo.");
            }
        } catch (e) {
            setSendError("No pudimos registrar tu calificación. Revisa tu conexión e intenta de nuevo.");
        } finally {
            setSending(false);
        }
    };

    if (loadError) {
        return (
            <Shell company={null} demo={demo}>
                <Message emoji="📡" title="No pudimos cargar la encuesta" text="Revisa tu conexión." />
                <button className="nps-send nps-send--full" style={{ "--btn-color": DEFAULT_COLOR }} onClick={load}>Reintentar</button>
            </Shell>
        );
    }
    if (!data) {
        return <Shell company={null}><div className="encuesta-loading"><span className="nps-spinner encuesta-spinner" /></div></Shell>;
    }
    if (done) {
        return <Shell company={company} demo={demo}><Message {...done} /></Shell>;
    }
    if (data.state !== "pending") {
        return <Shell company={company} demo={demo}><Message {...(STATE_MESSAGES[data.state] || STATE_MESSAGES.invalid)} /></Shell>;
    }

    return (
        <Shell company={company} demo={demo}>
            <div className="nps-header">
                <h2 className="nps-title">¿Qué tan probable es que recomiendes {company.name} a un amigo o familiar?</h2>
                <p className="nps-subtitle">
                    Pedido #{data.order_number}{data.branch_name ? ` · ${data.branch_name}` : ""}
                </p>
            </div>

            <div className="encuesta-scale" role="radiogroup" aria-label="Puntaje de 0 a 10">
                {Array.from({ length: 11 }, (_, n) => (
                    <button
                        key={n}
                        type="button"
                        role="radio"
                        aria-checked={score === n}
                        className={`encuesta-score ${score === n ? "encuesta-score--active" : ""}`}
                        style={{ "--score-color": scoreColor(n) }}
                        onClick={() => setScore(n)}
                    >
                        {n}
                    </button>
                ))}
            </div>
            <div className="encuesta-scale-legend">
                <span>Nada probable</span>
                <span>Muy probable</span>
            </div>

            {score !== null && (
                <div className="nps-comment-wrap">
                    <label className="nps-comment-label" htmlFor="encuesta-comment">
                        {score >= 9 ? "¿Qué fue lo que más te gustó? (opcional)" : "¿Qué podríamos mejorar? (opcional)"}
                    </label>
                    <textarea
                        id="encuesta-comment"
                        className="nps-comment"
                        placeholder="Cuéntanos más..."
                        value={comment}
                        maxLength={1000}
                        onChange={(e) => setComment(e.target.value)}
                        rows={3}
                        style={{ "--focus-color": brand }}
                    />
                </div>
            )}

            {sendError && <p className="encuesta-error">{sendError}</p>}

            <button
                className="nps-send nps-send--full"
                onClick={handleSend}
                disabled={score === null || sending}
                style={{ "--btn-color": brand, "--btn-text": "#fff" }}
            >
                {sending ? <span className="nps-spinner" /> : "Enviar calificación"}
            </button>
        </Shell>
    );
}

export function EncuestaBaja() {
    const { enlace } = useParams();
    const demo = enlace === DEMO;
    const demoCompany = useDemoCompany();
    const [sending, setSending] = useState(false);
    const [result, setResult] = useState(null); // {company?, message}
    const [error, setError] = useState("");

    const confirm = async () => {
        setError("");
        if (demo) {
            setResult({ company: demoCompany, ok: true });
            return;
        }
        setSending(true);
        try {
            const res = await surveyRequest(`/survey/email/${encodeURIComponent(enlace)}/unsubscribe`, {});
            if (res?.success === "success") setResult({ company: res?.data?.company || null, ok: true });
            else if (res?.data?.state === "invalid") setResult({ company: null, ok: false });
            else setError(res?.message || "No pudimos procesar tu solicitud. Intenta de nuevo.");
        } catch (e) {
            setError("No pudimos procesar tu solicitud. Revisa tu conexión e intenta de nuevo.");
        } finally {
            setSending(false);
        }
    };

    if (result) {
        return (
            <Shell company={result.company} demo={demo}>
                {result.ok ? (
                    <Message
                        emoji="✅"
                        title="Listo, te diste de baja"
                        text={`No te enviaremos más encuestas de satisfacción${result.company?.name ? ` de ${result.company.name}` : ""}. Las comunicaciones necesarias sobre tus pedidos no cambian.`}
                    />
                ) : (
                    <Message {...STATE_MESSAGES.invalid} />
                )}
            </Shell>
        );
    }

    return (
        <Shell company={demo ? demoCompany : null} demo={demo}>
            <Message emoji="✉️" title="¿Dejar de recibir estas encuestas?" text="Esta preferencia únicamente desactiva las encuestas de satisfacción de este comercio. No afecta las comunicaciones necesarias relacionadas con tus pedidos." />
            {error && <p className="encuesta-error">{error}</p>}
            <button className="nps-send nps-send--full" style={{ "--btn-color": DEFAULT_COLOR }} onClick={confirm} disabled={sending}>
                {sending ? <span className="nps-spinner" /> : "Sí, darme de baja"}
            </button>
        </Shell>
    );
}
